import React from 'react';
import { reduce } from 'lodash-es';
import helpers, { parsePeriodFilters } from '../../../../utils/helpers';
import InputDropdown from '../../../../components/ASOComponents/InputDropdown';
import { format, differenceInDays, addWeeks, addMonths, addQuarters, subDays } from '../../../../utils/date';
import { getGlobalSettings } from '../../../../utils/global_settings';

export const dailyPeriodThreshold = 186;
export const analyzedLanguages = ['en', 'es', 'pt'];

const { currentUser, permissions: globalPermissions } = getGlobalSettings();

export const initDataParams = ({
  refNo,
  store,
  initData,
  defaultPeriod,
  periodFilters,
  maxPeriod,
  maxDateTo,
}) => ({
  permissions: {
    ...globalPermissions,
    current_user_id: currentUser.id,
    customer_id: currentUser.customerId,
  },
  currentUserId: currentUser.id,
  refNo,
  store,
  assets: JSON.parse(initData).assets,
  defaultPeriod: parseInt(defaultPeriod, 10),
  periodFilters: parsePeriodFilters(periodFilters),
  maxPeriod: maxPeriod ? parseInt(maxPeriod, 10) : 365,
  maxDateTo,
});

// Trends
export const trends = ['performance-and-bugs', 'design', 'service', 'login', 'feature-request', 'support', 'payment', 'notifications'];
export const trendsColors = {
  'performance-and-bugs': '#0B4FBA',
  design: '#8EEDE6',
  service: '#CDABF9',
  login: '#A90051',
  'feature-request': '#F24979',
  support: '#008A45',
  payment: '#99C1FF',
  notifications: '#3382FF',
};

export const trendsList = {
  'performance-and-bugs': 'Performance & Bugs',
  design: 'Design',
  service: 'Service',
  login: 'Login',
  'feature-request': 'Feature Requests',
  support: 'Support',
  payment: 'Payment',
  notifications: 'Notifications',
};

export const trendsMapping = reduce(
  trendsList,
  (memo, text, value) => ([...memo, { value, label: text, text }]),
  [],
);

export const trendsWithColor = trendsMapping.map(trend => ({
  ...trend,
  color: trendsColors[trend.value],
}));

// Sentiments
export const sentiments = ['positive', 'neutral', 'mixed', 'negative'];

export const seriesColor = {
  positive: '#1FCC67',
  neutral: '#3382FF',
  mixed: '#9B57F2',
  negative: '#F24979',
  overall: '#0144AE',
};

export const seriesList = {
  positive: 'Positive',
  neutral: 'Neutral',
  mixed: 'Mixed',
  negative: 'Negative',
  overall: 'Overall Sentiment',
};

const seriesMapping = reduce(
  seriesList,
  (memo, text, value) => ([...memo, { value, text }]),
  [],
);

export const seriesWithColor = seriesMapping.map(series => ({
  ...series,
  color: seriesColor[series.value],
}));

export const sentimentOptions = [
  { label: 'Positive', value: 'positive' },
  { label: 'Neutral', value: 'neutral' },
  { label: 'Mixed', value: 'mixed' },
  { label: 'Negative', value: 'negative' },
];

// Ratings
export const ratings = ['1', '2', '3', '4', '5'];

export const ratingsColor = {
  rating_1: '#ED5588',
  rating_2: '#FE956C',
  rating_3: '#A55DC7',
  rating_4: '#348BE3',
  rating_5: '#3CBD67',
  rating_moving_average_rating: '#15385B',
};

export const ratingsList = {
  rating_1: '⭐',
  rating_2: '⭐⭐',
  rating_3: '⭐⭐⭐',
  rating_4: '⭐⭐⭐⭐',
  rating_5: '⭐⭐⭐⭐⭐',
  rating_moving_average_rating: 'Average Rating (30 day)',
};

export const ratingsOptions = [
  { label: '★', value: 1 },
  { label: '★★', value: 2 },
  { label: '★★★', value: 3 },
  { label: '★★★★', value: 4 },
  { label: '★★★★★', value: 5 },
];

export const storeLegendOptions = [
  { id: 'primary', name: 'iOS', color: helpers.storeColors.itunes_connect },
  { id: 'secondary', name: 'Google Play', color: helpers.storeColors.google_play },
];

export const benchmarksColors = {
  primary: '#0144AE',
  secondary: '#00E1D4',
};

export const getPeriodByGranularity = (date, granularity) => {
  if (!date) return;

  let date_to;
  let period = 1;

  const granularityMapping = {
    weekly: addWeeks,
    monthly: addMonths,
    quarterly: addQuarters,
  };

  if (granularity === 'daily') date_to = date;
  else {
    date_to = subDays(granularityMapping[granularity](date, 1), 1);
    period = differenceInDays(date_to, date);
  }

  // eslint-disable-next-line consistent-return
  return {
    period_from_date: format(date),
    period_to_date: format(date_to),
    period,
  };
};

const inputNumberPattern = '[0-9]*';
export const keywordsTableColumns =
  (frequency, onFrequencyChange, validFrequency, minFrequency, maxFrequency) => ([
    {
      dataField: 'keyword',
      text: 'keyword',
    },
    {
      dataField: 'impact_score',
      sortBy: 'impact_score',
      sortable: true,
      text: '',
      tooltipText: 'Impact Score is a weighted index that measures the effect a term has on Sentiment. We use absolute values for sorting purposes, so that you can see the most impactful terms easily. The bar to the left of the score indicates if the term has a positive or negative effect on Sentiment.',
      entireCellTooltip: true,
      element:
        (
          <p className="d-flex align-items-center">
            impact score
            <i className="hicon-question-circle text-16 ml-xxs mr-xxxs" />
            <span className="text-green m-l-xxxs text-11">BETA</span>
          </p>
        ),
    },
    {
      dataField: 'reviews_count',
      text: '',
      sortBy: 'reviews_count',
      sortable: true,
      element: (
        <div className="d-flex align-items-center">
          <span className="flex-grow">Frequency</span>
          <div onClick={e => e.stopPropagation()}>
            <InputDropdown
              value={frequency}
              onChange={onFrequencyChange}
              min={minFrequency}
              max={maxFrequency}
              headerText="Minimum"
              inputPattern={inputNumberPattern}
              valid={validFrequency}
            />
          </div>
        </div>
      ),
    },
    {
      dataField: 'sub_keywords',
      text: 'people also mention',
    },
    {
      dataField: 'reviews',
      text: 'Reviews',
    },
  ]);

export const columns = (type, dropdown, metric) => {
  if (type === 'keyword') {
    return [
      {
        dataField: 'date',
        sortBy: 'post_date',
        text: 'Date',
        sortable: true,
      },
      {
        dataField: 'keywords',
        text: 'Keywords',
      },
      {
        dataField: 'reviews',
        sortBy: 'body',
        text: 'Reviews',
      },
      {
        dataField: 'version',
        sortBy: 'app_version',
        text: 'Version',
        sortable: true,
        className: 'justify-content-end',
      },
    ];
  }
  return [
    {
      dataField: 'date',
      sortBy: 'post_date',
      sortable: true,
      text: 'Date',
    },
    {
      dataField: 'intent',
      text: '',
      element: (
        <div className="d-flex align-items-center">
          <span>intent</span>
          {dropdown('topic')}
        </div>
      ),
    },
    {
      dataField: metric === 'ratings_breakdown' ? 'stars' : 'sentiment',
      text: '',
      element: (
        <div className="d-flex align-items-center">
          <span>{metric === 'ratings_breakdown' ? 'rating' : 'sentiment'}</span>
          {dropdown(metric === 'ratings_breakdown' ? 'ratings' : 'sentiment')}
        </div>
      ),
    },
    {
      dataField: 'reviews',
      sortBy: 'body',
      text: 'Reviews',
    },
    {
      dataField: 'version',
      sortBy: 'app_version',
      text: 'Version',
      className: 'justify-content-end',
      sortable: true,
    },
    {
      dataField: 'keywords',
      text: 'Keywords',
    },
  ];
};
